import { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { visitorService } from '../services/visitorService';

interface VisitorFormProps {
  onSubmit: () => void;
}

interface FormData {
  firstName: string;
  email: string;
}

export function VisitorForm({ onSubmit }: VisitorFormProps) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    email: ''
  });
  const [errors, setErrors] = useState({
    firstName: '',
    email: ''
  });

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    const newErrors = {
      firstName: formData.firstName.length < 2 ? 'Name must be at least 2 characters' : '',
      email: !validateEmail(formData.email) ? 'Please enter a valid email' : ''
    };

    setErrors(newErrors);

    if (newErrors.firstName || newErrors.email) {
      return;
    }

    const success = await visitorService.saveVisitorData(formData.firstName, formData.email);
    
    if (success) {
      // Store in localStorage for future visits
      localStorage.setItem('visitorName', formData.firstName);
      localStorage.setItem('visitorEmail', formData.email);
      onSubmit();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg p-6 max-w-md w-full"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Welcome to .NET Mastery!</h2>
          <button
            onClick={onSubmit}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Please provide your details to get started with our .NET learning platform.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              placeholder="Enter your first name"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              placeholder="Enter your email"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors"
          >
            Get Started
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
} 